import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import ChartFrame from "@/components/ChartFrame";
import { Star, ClipboardEdit } from "lucide-react";
import { SELFEVAL_SECTIONS, sectionAverage, ratingColour } from "@/lib/selfEval";
import { TOOLTIP_STYLE_NUNITO, TOOLTIP_LABEL, TICK_MICRO, INIT_DIM_SM } from "@/lib/chartStyles";

const TINT = "#9A7AA0";
const DOMAIN_0_9 = [0, 9];
const DOT_PURPLE = { r: 3, fill: TINT };

const overallAvg = (ratings) => {
  const a = SELFEVAL_SECTIONS.map((s) => sectionAverage(ratings, s)).filter((v) => v != null);
  if (!a.length) return null;
  return Math.round((a.reduce((x, y) => x + y, 0) / a.length) * 10) / 10;
};
const descriptor = (v) => (v == null ? "" : v <= 3 ? "Emerging" : v <= 6 ? "Developing" : "Confident");

export default function SelfEvalCard({ studentId }) {
  const navigate = useNavigate();
  const [evals, setEvals] = useState(null);

  useEffect(() => {
    api.get(`/self-evaluations?student_id=${studentId}`).then((r) => setEvals(r.data)).catch(() => setEvals([]));
  }, [studentId]);

  const list = evals || [];
  const latest = list[0]; // API returns newest first
  const chartData = [...list].reverse().map((e) => ({
    date: new Date(e.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short" }),
    avg: overallAvg(e.ratings),
  })).filter((d) => d.avg != null);
  const latestAvg = latest ? overallAvg(latest.ratings) : null;
  const goto = () => navigate(latest ? `/student/${studentId}/self-eval/${latest.id}` : `/student/${studentId}/self-eval`);

  return (
    <div data-testid="subject-card-selfeval" className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: TINT }}>
            <Star size={13} /> Get to Know Me
          </p>
          {latestAvg != null ? (
            <span data-testid="selfeval-card-pill" className="inline-block mt-1 text-sm font-bold text-white rounded-full px-3 py-1" style={{ backgroundColor: ratingColour(Math.round(latestAvg)) }}>
              {descriptor(latestAvg)} · {latestAvg}
            </span>
          ) : (
            <span className="text-sm text-slate-400">Not completed</span>
          )}
        </div>
        {latestAvg != null && (
          <div className="h-12 w-12 rounded-full flex items-center justify-center font-['Outfit'] font-bold text-white" style={{ backgroundColor: TINT }}>
            {latestAvg}
          </div>
        )}
      </div>

      {latest ? (
        <>
          <div className="grid grid-cols-5 gap-1.5 mt-5">
            {SELFEVAL_SECTIONS.map((s) => {
              const v = sectionAverage(latest.ratings, s) || 0;
              return (
                <div key={s.key} className="text-center" title={`${s.title}: ${v || "—"}/9`}>
                  <div className="h-16 rounded-lg flex items-end overflow-hidden bg-slate-50">
                    <div className="w-full rounded-t-lg" style={{ height: `${(v / 9) * 100}%`, backgroundColor: ratingColour(Math.round(v)) }} />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-1 leading-tight truncate">{s.emoji}</p>
                </div>
              );
            })}
          </div>

          {chartData.length > 1 && (
            <ChartFrame className="h-20 mt-4 -ml-2">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={INIT_DIM_SM}>
                <LineChart data={chartData}>
                  <Tooltip contentStyle={TOOLTIP_STYLE_NUNITO} labelStyle={TOOLTIP_LABEL} />
                  <XAxis dataKey="date" tick={TICK_MICRO} axisLine={false} tickLine={false} />
                  <YAxis domain={DOMAIN_0_9} hide />
                  <Line type="monotone" dataKey="avg" stroke={TINT} strokeWidth={2.5} dot={DOT_PURPLE} />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Last completed {new Date(latest.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })} · {list.length} record{list.length > 1 ? "s" : ""}
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-400 mt-5 flex-1">A pupil self-reflection on social &amp; emotional skills — a lovely baseline to revisit later.</p>
      )}

      <button data-testid="open-selfeval-card-btn" onClick={goto}
        className="mt-5 w-full rounded-full py-2.5 font-semibold font-['Outfit'] text-white transition hover:opacity-90 flex items-center justify-center gap-2"
        style={{ backgroundColor: TINT }}>
        <ClipboardEdit size={17} /> {latest ? "Open self-evaluation" : "Start self-evaluation"}
      </button>
    </div>
  );
}
